import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import InternalNoteReport, { getInternalNoteReportRows } from './Chat/InternalNoteReport';
import { CustomSelect } from './CustomFormElements';
import DateRangeSelector from './DateRangeSelector';
import Table from './Tables';
import { useChannelApi } from '../Services/ChannelApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import { formatTableTitle } from '../utils/formatTitle';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';

export interface ChannelReportApi {
  id: string;
  chat_id: string;
  chat_name: string;
  report_type: string | null;
  // Missing on reports saved before the writing agent was recorded
  assistant_key: string | null;
  report: Record<string, unknown>;
  text: string;
  created_at: string;
}

interface ChannelReportsListProps {
  channelId: string;
  agents: { key: string; name: string }[];
}

const TABLE_TITLE = 'Reports';
const ALL_AGENTS = '';

// The table keeps its page size in storage; a new filter reuses it from the first page
const getPageSize = (): number =>
  getTablePaginationFromStorage(formatTableTitle(TABLE_TITLE)).persistedPageSize;

function ReportDetails({ data }: { data: ChannelReportApi }) {
  const rows = getInternalNoteReportRows(data.report);

  return (
    <div className="table-folded-content">
      {rows.length > 0 ? (
        <InternalNoteReport rows={rows} />
      ) : (
        <div style={{ whiteSpace: 'pre-wrap' }}>{data.text}</div>
      )}
    </div>
  );
}

function ChannelReportsList({ channelId, agents }: ChannelReportsListProps) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { getChannelReports, exportChannelReports } = useChannelApi();
  const { showAlert } = useAlert();

  const [reports, setReports] = useState<ChannelReportApi[]>([]);
  const [selectedRows, setSelectedRows] = useState<ChannelReportApi[]>([]);
  const [totalReports, setTotalReports] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [agentKey, setAgentKey] = useState<string>(ALL_AGENTS);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string | null>(null);
  const [resetPagination, setResetPagination] = useState<boolean>(false);

  // Only the latest request may fill the table, so a slow earlier one cannot overwrite it
  const lastRequest = useRef(0);

  const agentNames = useMemo(
    () => new Map(agents.map((agent) => [agent.key, agent.name])),
    [agents],
  );

  const agentOptions = useMemo(
    () => [
      { value: ALL_AGENTS, label: 'All agents' },
      ...agents.map((agent) => ({ value: agent.key, label: agent.name })),
    ],
    [agents],
  );

  const columns = [
    { name: 'Chat', selector: (row: ChannelReportApi) => row.chat_name, sortable: false },
    {
      name: 'Report type',
      selector: (row: ChannelReportApi) => row.report_type ?? '',
      sortable: false,
    },
    {
      name: 'Agent',
      selector: (row: ChannelReportApi) =>
        row.assistant_key ? (agentNames.get(row.assistant_key) ?? 'Unknown agent') : '—',
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: ChannelReportApi) => formatDateTimeLocal(row.created_at),
      sortable: false,
    },
  ];

  const fetchReports = async (skip: number, limit: number): Promise<void> => {
    if (!startDate) return;

    const request = ++lastRequest.current;
    setLoading(true);
    setError(null);

    try {
      const response = await getChannelReports(channelId, {
        skip,
        limit,
        assistantKey: agentKey || undefined,
        startDate,
        endDate,
      });
      if (request !== lastRequest.current) return;
      setReports(response.items ?? []);
      setTotalReports(response.totalCount ?? 0);
    } catch (err) {
      if (request !== lastRequest.current) return;
      console.error('Error fetching channel reports:', err);
      setError('Failed to load reports');
    } finally {
      if (request === lastRequest.current) setLoading(false);
    }
  };

  // A changed filter starts over from the first page. Rebuilding the API client does not refetch.
  useEffect(() => {
    if (!startDate) return;
    setResetPagination((previous) => !previous);
    fetchReports(0, getPageSize());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, agentKey, startDate, endDate]);

  const handlePeriodChange = useCallback(
    (period: { start_date: string; end_date: string | null }) => {
      setStartDate(period.start_date);
      setEndDate(period.end_date);
    },
    [],
  );

  // Choosing the selected agent again clears the choice
  const handleAgentChange = (value: string | string[] | null): void =>
    setAgentKey(typeof value === 'string' ? value : ALL_AGENTS);

  const handleExport = async (): Promise<void> => {
    if (selectedRows.length === 0) return;
    try {
      await exportChannelReports(
        channelId,
        selectedRows.map((row) => row.id),
      );
    } catch (err) {
      showAlert('fail', 'Failed to export the reports.');
    }
  };

  const chatUrl = (row: ChannelReportApi): string =>
    `/organizations/${organizationId}/chats/${row.chat_id}`;

  // Cmd/Ctrl-click already opened the chat in a new tab
  const handleRowClicked = (row: ChannelReportApi, event?: React.MouseEvent): void => {
    if (event?.metaKey || event?.ctrlKey) return;
    navigate(chatUrl(row));
  };

  return (
    <div>
      <div className="p-2 border-yes radius-yes mb-3">
        <h4 className="h4-title">Filters</h4>
        <div className="gap d-flex folded">
          <div className="filter-col">
            <CustomSelect
              id="channel-reports-agent-filter"
              label="Agent"
              options={agentOptions}
              value={agentKey}
              onChange={handleAgentChange}
            />
          </div>
        </div>
        <DateRangeSelector
          storageKey="channel-reports"
          defaultPeriod="month"
          onChange={handlePeriodChange}
        />
      </div>

      {error ? (
        <div>Error: {error}</div>
      ) : (
        <Table
          title={TABLE_TITLE}
          columns={columns}
          data={reports}
          progressPending={loading}
          paginationPerPage={getPageSize()}
          paginationTotalRows={totalReports}
          resetPaginationToggle={resetPagination}
          expandableRows
          expandableRowsComponent={ReportDetails}
          onRowClicked={handleRowClicked}
          onSelectedRowsChange={({ selectedRows: rows }: { selectedRows: ChannelReportApi[] }) =>
            setSelectedRows(rows)
          }
          contextActionsExtra={
            <Button
              variant="ghost"
              size="medium"
              disabled={selectedRows.length === 0}
              onClick={handleExport}
            >
              Export Selected
            </Button>
          }
          selectableRows
          singleSelect={false}
          selectableRowsHighlight
          hideDeleteSelected
          getRowUrl={chatUrl}
          onChangePage={fetchReports}
        />
      )}
    </div>
  );
}

export default ChannelReportsList;
